/**
 * In-process token cache for GitHub App installation tokens.
 *
 * Lives in the importing process — one cache per Container App replica
 * (gateway) and one per Claude Code session (local MCP plugin). Acceptable
 * because installation tokens rotate every ~50 min (60 min TTL − 10 min
 * safety margin); cold-start cost is one round-trip; replica/session
 * isolation is a tolerable tradeoff for no shared-state infra.
 */

export const EXPIRY_SAFETY_MS = 10 * 60 * 1000; // refresh if <10 min remaining

export type InstallationPermissions = Readonly<Record<string, string>>;

interface Entry {
  token: string;
  expiresAt: Date;
  permissions?: Record<string, string>;
  repositorySelection?: "all" | "selected";
}
const cache = new Map<string, Entry>();

/** Stable permission identity: key order must not change cache semantics. */
export function canonicalPermissions(
  permissions: InstallationPermissions | undefined,
): string {
  if (permissions === undefined) return "<installation-defaults>";
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(permissions).sort(([left], [right]) => left.localeCompare(right)),
    ),
  );
}

function key(
  installationId: string,
  repository: string,
  permissions: InstallationPermissions | undefined,
): string {
  return `${installationId}:${repository}:${canonicalPermissions(permissions)}`;
}

/** TEST-ONLY: clear the cache between tests. */
export function _resetTokenCache(): void {
  cache.clear();
}

/**
 * Look up a cached token. Returns null if missing, expired, or within the
 * safety margin (caller must mint fresh).
 */
export function getCachedToken(
  installationId: string,
  repository: string,
  permissions?: InstallationPermissions,
): Entry | null {
  const e = cache.get(key(installationId, repository, permissions));
  if (!e) return null;
  if (e.expiresAt.getTime() - Date.now() <= EXPIRY_SAFETY_MS) return null;
  return e;
}

/** Cache a freshly-minted token. */
export function putCachedToken(
  installationId: string,
  repository: string,
  token: string,
  expiresAt: Date,
  permissions?: InstallationPermissions,
  result?: {
    permissions?: Record<string, string>;
    repositorySelection?: "all" | "selected";
  },
): void {
  cache.set(key(installationId, repository, permissions), {
    token,
    expiresAt,
    ...(result?.permissions ? { permissions: { ...result.permissions } } : {}),
    ...(result?.repositorySelection
      ? { repositorySelection: result.repositorySelection }
      : {}),
  });
}
